import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';
import {
  Alert, Button, Card, SearchableSelect, StatusBadge, Tabs, useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { Stock, StockCreate } from '@kss/api';
import { stockService, productService, storeService, warehouseService } from '@kss/api';
import NumberField from '../../components/NumberField';
import { useModulePerms } from '../../hooks/useModulePerms';

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
type FormState = { productId: string; storeId: string; warehouseId: string; quantity: number; reorderLevel: number; isActive: boolean };
const emptyForm: FormState = { productId: '', storeId: '', warehouseId: '', quantity: 0, reorderLevel: 0, isActive: true };

const STEPS = ['product', 'location', 'levels', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  product: 'Product', location: 'Location', levels: 'Levels & status', review: 'Review & save',
};

const loadProductOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await productService.all();
  return (rows ?? []).map((p) => ({ value: p.productId ?? '', label: p.name, description: p.sku, disabled: !p.productId }));
};
const loadStoreOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await storeService.all();
  return (rows ?? []).map((s) => ({ value: s.storeId ?? '', label: s.name, description: s.code, disabled: !s.storeId }));
};
const loadWarehouseOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await warehouseService.all();
  return (rows ?? []).map((w) => ({ value: w.warehouseId ?? '', label: w.name, description: w.code, disabled: !w.warehouseId }));
};

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function StockForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const perms = useModulePerms('Stock');
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('product');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setLoadError(null);
        try {
          const rec = await stockService.get(id);
          if (cancelled) return;
          setForm({
            productId: rec.productId ?? '', storeId: rec.storeId ?? '', warehouseId: rec.warehouseId ?? '',
            quantity: Number(rec.quantity ?? 0), reorderLevel: Number(rec.reorderLevel ?? 0), isActive: !!rec.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
      setErrors({}); setStep('product'); setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const validateStep = (s: StepId) => {
    const errs: typeof errors = {};
    if (s === 'product' || s === 'review') if (!form.productId) errs.productId = 'Select a product';
    if (s === 'levels' || s === 'review') if (Number(form.quantity) < 0) errs.quantity = 'Must be ≥ 0';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const goNext = () => {
    const i = STEPS.indexOf(step); if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1];
    setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
    setStep(next);
  };
  const goPrev = () => {
    const i = STEPS.indexOf(step); if (i === 0) return;
    setRedTabs((p) => new Set(p).add(step)); setStep(STEPS[i - 1]);
  };
  const basicValid = !!form.productId;

  const handleSave = async () => {
    // Tab-wise: each tab only validates its own fields; Save & Next
    // on intermediate tabs just advances. API save runs on Review.
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const stepIdx = STEPS.indexOf(step);
    const isLastStep = stepIdx === STEPS.length - 1;
    if (!isLastStep) {
      const next = STEPS[stepIdx + 1];
      setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
      setStep(next);
      return;
    }
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    setSaving(true);
    try {
      // Live `POST /api/Stock` rejects `isActive` with `UNKNOWN_FIELD`
      // (verified 2026-05-05) — Stock rows are deactivated by clearing
      // their owning product, not via a per-row flag. The form keeps
      // an Active toggle for UI affordance only; we strip it from the
      // wire payload here.
      if (isEdit && id) {
        const p: Stock = {
          stockId: id,
          productId: form.productId,
          storeId: form.storeId || undefined,
          warehouseId: form.warehouseId || undefined,
          quantity: Number(form.quantity),
          reorderLevel: Number(form.reorderLevel),
        };
        await stockService.update([p]);
        toast.success('Saved', 'Stock updated');
      } else {
        const p: StockCreate = {
          productId: form.productId,
          storeId: form.storeId || undefined,
          warehouseId: form.warehouseId || undefined,
          quantity: Number(form.quantity),
          reorderLevel: Number(form.reorderLevel),
        };
        // Live `POST /api/Stock` returns `data: true` instead of the
        // created entity. Recover the id by filtering on (productId,
        // warehouseId) — the natural unique key for a stock row. Do NOT
        // return early on miss, otherwise Save & Next is swallowed.
        await stockService.create([p]);
        toast.success('Saved', 'Stock added');
      }
      // Review-tab save succeeded — back to the list (matches edit mode).
      navigate('/panel/stock');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => { const m: Record<string, TabStatus> = {}; redTabs.forEach((s) => (m[s] = 'danger')); return m; }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label} {required && <span className="text-danger">*</span>}</span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const productStep = (
    <div className="grid gap-3">
      <SearchableSelect label="Product" value={form.productId} onChange={(v) => set('productId', v)}
        loadOptions={loadProductOptions} placeholder="Select a product" searchPlaceholder="Search products..." required error={errors.productId} />
    </div>
  );
  const locationStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect label="Store" value={form.storeId} onChange={(v) => set('storeId', v)}
        loadOptions={loadStoreOptions} placeholder="Select a store" searchPlaceholder="Search stores..." />
      <SearchableSelect label="Warehouse" value={form.warehouseId} onChange={(v) => set('warehouseId', v)}
        loadOptions={loadWarehouseOptions} placeholder="Select a warehouse" searchPlaceholder="Search warehouses..." />
    </div>
  );
  const levelsStep = (
    <div className="grid gap-3 md:grid-cols-3">
      {field('Quantity', 'quantity',
        <NumberField min={0} value={form.quantity} onValueChange={(n) => set('quantity', n)} className={inputCls} />, true)}
      {field('Reorder level', 'reorderLevel',
        <NumberField min={0} value={form.reorderLevel} onValueChange={(n) => set('reorderLevel', n)} className={inputCls} />)}
      {field('Status', 'isActive',
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>)}
    </div>
  );
  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Ready to save</span></div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Product" value={<NameCell kind="product" id={form.productId} />} />
          <Row label="Store" value={<NameCell kind="store" id={form.storeId} />} />
          <Row label="Warehouse" value={<NameCell kind="warehouse" id={form.warehouseId} />} />
          <Row label="Quantity" value={form.quantity} />
          <Row label="Reorder level" value={form.reorderLevel} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'product' as const, label: STEP_LABELS.product, content: productStep },
    { id: 'location' as const, label: STEP_LABELS.location, content: locationStep },
    { id: 'levels' as const, label: STEP_LABELS.levels, content: levelsStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus} barOnly />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/stock')}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!basicValid || saving || (isLast && !canSave)}
              title={isLast && !canSave ? `You don't have permission to ${isEdit ? 'update' : 'create'} stock` : undefined}
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value !== '' && value != null ? value : <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
